export enum Locale {
  en_ZZ = 'en_ZZ',
  es_ZZ = 'es_ZZ',
  de_DE = 'de_DE',
  de_AT = 'de_AT',
  de_CH = 'de_CH',
  en_DE = 'en_DE',
  fr_CH = 'fr_CH',
  it_CH = 'it_CH',
  nl_NL = 'nl_NL',
  nl_BE = 'nl_BE',
  fr_BE = 'fr_BE',
  pl_PL = 'pl_PL',
  cs_CZ = 'cs_CZ',
  sk_SK = 'sk_SK',
  ro_RO = 'ro_RO',
  hu_HU = 'hu_HU',
  sl_SI = 'sl_SI',
  lv_LV = 'lv_LV',
  ru_LV = 'ru_LV',
  lt_LT = 'lt_LT',
  et_EE = 'et_EE',
  ru_EE = 'ru_EE',
  hr_HR = 'hr_HR',
  bg_BG = 'bg_BG',
  da_DK = 'da_DK',
  en_IE = 'en_IE',
  fr_FR = 'fr_FR',
  sv_SE = 'sv_SE',
  fi_FI = 'fi_FI',
  es_ES = 'es_ES',
  it_IT = 'it_IT',
  pt_PT = 'pt_PT',
  el_GR = 'el_GR',
  no_NO = 'no_NO',
  ar_SA = 'ar_SA',
  en_SA = 'en_SA',
  he_IL = 'he_IL',
  en_IL = 'en_IL',
  el_CY = 'el_CY',
  fr_LU = 'fr_LU',
}

export type THrefLang =
  | 'de-DE'
  | 'en-DE'
  | 'de-AT'
  | 'de-CH'
  | 'fr-CH'
  | 'it-CH'
  | 'pl-PL'
  | 'cs-CZ'
  | 'nl-NL'
  | 'nl-BE'
  | 'fr-BE'
  | 'ro-RO'
  | 'hu-HU'
  | 'sk-SK'
  | 'sl-SI'
  | 'lv-LV'
  | 'ru-LV'
  | 'lt-LT'
  | 'et-EE'
  | 'ru-EE'
  | 'hr-HR'
  | 'bg-BG'
  | 'fr-FR'
  | 'en-IE'
  | 'da-DK'
  | 'sv-SE'
  | 'fi-FI'
  | 'es-ES'
  | 'it-IT'
  | 'pt-PT'
  | 'el-GR'
  | 'no-NO'
  | 'fr-LU';

export type THrefLangByLocale = {
  locale: Locale;
  hrefLang: THrefLang;
};

export type CountryOf<ThisLocale extends Locale> =
  ThisLocale extends `${string}_${infer Country}` ? Country : never;

export type LanguageOf<ThisLocale extends Locale> =
  ThisLocale extends `${infer Language}_${string}` ? Language : never;

export type AnyCountry = CountryOf<Locale>;
export type AnyLanguage = LanguageOf<Locale>;

export function getCountry<SpecificLocale extends Locale>(
  locale: SpecificLocale,
) {
  return locale.split('_')[1]! as CountryOf<SpecificLocale>;
}

export function getLanguage<SpecificLocale extends Locale>(
  locale: SpecificLocale,
) {
  return locale.split('_')[0]! as LanguageOf<SpecificLocale>;
}

/**
 * Specific to Help Center Locales
 *
 * locale mapping definition
 * taken from https://docs.google.com/spreadsheets/d/1vW7vTHbZDBf-MFPVnAQTy7ImWpE2EZliK_J4Er3fvd8
 * AYSA      Zendesk locale
 * de_DE     de
 * de_AT     de-at
 * de_CH     de-ch
 * es_ES     es-es
 * fr_CH     fr-ch
 * it_IT     it-it
 * nl_NL     nl
 * nl_BE     nl-be
 * fr_BE     fr-be
 * pl_PL     pl
 * cs_CZ     cs
 * sk_SK     sk
 * ro_RO     ro
 * hu_HU     hu
 * sl_SI     sl
 * lv_LV     lv
 * lt_LT     lt
 * et_EE     et
 * hr_HR     hr
 * bg_BG     bg
 * da_DK     da
 * en_IE     en-ie
 * fr_FR     fr
 * sv_SE     sv
 * fi_FI     fi
 *
 */

export enum HelpCenterLocale {
  bg = 'bg',
  cs = 'cs',
  da = 'da',
  de = 'de',
  de_at = 'de-at',
  it_ch = 'it-ch',
  de_ch = 'de-ch',
  en_de = 'en-de',
  en_001 = 'en-001',
  da_DK = 'en-dk',
  en_ie = 'en-ie',
  sv_SE = 'en-se',
  en_us = 'en-us',
  es_es = 'es-es',
  en_sa = 'en-ps',
  en_il = 'en-il',
  es = 'es',
  et = 'et',
  ru_ee = 'ru-ee',
  fi = 'fi',
  fr = 'fr',
  fr_be = 'fr-be',
  fr_ch = 'fr-ch',
  fr_fr = 'fr-fr',
  ga = 'ga',
  hr = 'hr',
  hu = 'hu',
  hu_hu = 'hu-hu',
  it = 'it-it',
  lt = 'lt',
  lv = 'lv',
  ru_lv = 'ru-lv',
  nl = 'nl',
  nl_be = 'nl-be',
  pl = 'pl',
  ro = 'ro',
  sk = 'sk',
  sl = 'sl',
  sv = 'sv',
  pt = 'pt-pt',
  gr = 'el-gr',
  no = 'no',
  ar = 'ar',
  he = 'he',
  el_cy = 'el-cy',
  fr_lu = 'fr-lu',
}

export const localeToHCLocale: Record<Locale, HelpCenterLocale> = {
  en_ZZ: HelpCenterLocale.en_001,
  es_ZZ: HelpCenterLocale.es,
  en_SA: HelpCenterLocale.en_sa,
  en_IL: HelpCenterLocale.en_il,
  de_DE: HelpCenterLocale.de,
  de_AT: HelpCenterLocale.de_at,
  de_CH: HelpCenterLocale.de_ch,
  fr_CH: HelpCenterLocale.fr_ch,
  it_CH: HelpCenterLocale.it_ch,
  en_DE: HelpCenterLocale.en_de,
  nl_NL: HelpCenterLocale.nl,
  nl_BE: HelpCenterLocale.nl_be,
  fr_BE: HelpCenterLocale.fr_be,
  pl_PL: HelpCenterLocale.pl,
  cs_CZ: HelpCenterLocale.cs,
  sk_SK: HelpCenterLocale.sk,
  ro_RO: HelpCenterLocale.ro,
  hu_HU: HelpCenterLocale.hu,
  sl_SI: HelpCenterLocale.sl,
  lv_LV: HelpCenterLocale.lv,
  ru_LV: HelpCenterLocale.ru_lv,
  lt_LT: HelpCenterLocale.lt,
  et_EE: HelpCenterLocale.et,
  ru_EE: HelpCenterLocale.ru_ee,
  hr_HR: HelpCenterLocale.hr,
  bg_BG: HelpCenterLocale.bg,
  da_DK: HelpCenterLocale.da,
  en_IE: HelpCenterLocale.en_ie,
  fr_FR: HelpCenterLocale.fr,
  sv_SE: HelpCenterLocale.sv,
  fi_FI: HelpCenterLocale.fi,
  es_ES: HelpCenterLocale.es_es,
  it_IT: HelpCenterLocale.it,
  pt_PT: HelpCenterLocale.pt,
  el_GR: HelpCenterLocale.gr,
  no_NO: HelpCenterLocale.no,
  ar_SA: HelpCenterLocale.ar,
  he_IL: HelpCenterLocale.he,
  el_CY: HelpCenterLocale.el_cy,
  fr_LU: HelpCenterLocale.fr_lu,
};
